import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowDown, Volume2, VolumeX, ChevronRight, Sparkles } from 'lucide-react';
import { Navbar } from '../components/layout/Navbar';
import { SEO } from '../components/SEO';

const VIDEO_BASE = 'https://res.cloudinary.com/dtqyyb6su/video/upload';
const VIDEO_PATH = 'v1778432741/entrepot-auto/notre-processus/processus_master.mp4';

// Eight section videos. All section text is sourced from i18n (`notreProcessus.steps.{n}`).
const SECTION_VIDEOS = [
  'v1778433431/entrepot-auto/notre-processus/step-1.mp4',
  'v1778435914/entrepot-auto/notre-processus/step-2.mp4',
  'v1778435637/entrepot-auto/notre-processus/step-3.mp4',
  'v1778433433/entrepot-auto/notre-processus/step-4.mp4',
  'v1778435269/entrepot-auto/notre-processus/step-5.mp4',
  'v1778435997/entrepot-auto/notre-processus/step-6.mp4',
  'v1778435242/entrepot-auto/notre-processus/step-7.mp4',
  'v1778435711/entrepot-auto/notre-processus/step-8.mp4',
];

// Aggressive transforms: 720p wide, eco quality, capped bitrate, progressive download for instant playback.
const sectionVideoUrl = (path) =>
  `${VIDEO_BASE}/w_720,q_auto:eco,br_400k,fl_progressive,f_mp4/${path}`;

const sectionPosterUrl = (path) =>
  `${VIDEO_BASE}/w_720,q_auto:low,so_0/${path.replace('.mp4', '.jpg')}`;

const HERO_VIDEO = `${VIDEO_BASE}/w_1280,q_auto:eco,br_700k,fl_progressive,f_mp4/${VIDEO_PATH}`;
const HERO_POSTER = `${VIDEO_BASE}/so_1,w_1600,q_auto:low/${VIDEO_PATH.replace('.mp4', '.jpg')}`;
const AMBIENT_AUDIO = `${VIDEO_BASE}/f_mp3/${VIDEO_PATH.replace('.mp4', '')}`;

// Section card: lazy-loaded video using IntersectionObserver
const SectionVideo = ({ idx, video, label, title, description, stepShort }) => {
  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const reverse = idx % 2 === 1;
  const sectionId = idx + 1;
  const url = sectionVideoUrl(video);
  const poster = sectionPosterUrl(video);

  useEffect(() => {
    if (!sectionRef.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          if (videoRef.current && !videoRef.current.src) {
            videoRef.current.src = url;
            videoRef.current.load();
          }
          videoRef.current?.play().catch(() => {});
        } else {
          videoRef.current?.pause();
        }
      },
      { threshold: 0.2, rootMargin: '200px 0px 0px 0px' }
    );
    obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, [url]);

  return (
    <section
      ref={sectionRef}
      id={`section-${sectionId}`}
      data-section-index={sectionId}
      data-testid={`process-section-${sectionId}`}
      className="relative min-h-screen flex items-center py-24 md:py-32 px-4 md:px-12 lg:pr-28 overflow-hidden"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            idx % 2 === 0
              ? 'radial-gradient(circle at 80% 20%, rgba(59,130,246,0.08) 0%, transparent 60%)'
              : 'radial-gradient(circle at 20% 80%, rgba(29,78,216,0.10) 0%, transparent 60%)',
        }}
      />

      <div
        className={`relative max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center ${
          reverse ? 'lg:[&>*:first-child]:order-2' : ''
        }`}
      >
        <div
          className={`lg:col-span-5 transition-all duration-1000 ease-out ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
        >
          <div className="inline-flex items-center gap-2 mb-6 px-4 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs font-bold tracking-[0.2em] uppercase">
            <span className="text-blue-400">{String(sectionId).padStart(2, '0')}</span>
            <span className="text-blue-300/60">/</span>
            <span>{label}</span>
          </div>
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-5 leading-[1.05] tracking-tight"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            {title}
          </h2>
          <p className="text-blue-100/60 text-base md:text-lg leading-relaxed max-w-md">
            {description}
          </p>
          <div className="mt-8 h-[1px] w-16 bg-gradient-to-r from-blue-500 to-transparent" />
        </div>

        <div
          className={`lg:col-span-7 transition-all duration-1000 ease-out delay-200 ${
            visible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-12 scale-[0.98]'
          }`}
        >
          <div className="relative rounded-3xl overflow-hidden bg-slate-900 aspect-video shadow-[0_25px_80px_-20px_rgba(59,130,246,0.35)] border border-white/5 ring-1 ring-white/5">
            <video
              ref={videoRef}
              poster={poster}
              muted
              loop
              playsInline
              preload="metadata"
              onLoadedData={() => setLoaded(true)}
              className="w-full h-full object-cover"
            />
            {!loaded && (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-900/50">
                <div className="animate-spin rounded-full h-10 w-10 border-2 border-blue-500/30 border-t-blue-500" />
              </div>
            )}
            <div className="absolute top-4 left-4 backdrop-blur-md bg-black/30 border border-white/10 px-3 py-1.5 rounded-full text-xs font-bold text-white tracking-wider">
              {stepShort} {String(sectionId).padStart(2, '0')}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const NotreProcessusPage = () => {
  const { t } = useTranslation();
  const heroVideoRef = useRef(null);
  const audioRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [audioOn, setAudioOn] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  // Build localized step list
  const steps = SECTION_VIDEOS.map((video, idx) => ({
    idx,
    video,
    label: t(`notreProcessus.steps.${idx + 1}.label`),
    title: t(`notreProcessus.steps.${idx + 1}.title`),
    description: t(`notreProcessus.steps.${idx + 1}.description`),
  }));
  const stepShort = t('notreProcessus.stepShort');

  useEffect(() => {
    heroVideoRef.current?.play().catch(() => {});
  }, []);

  useEffect(() => {
    const observers = [];
    steps.forEach((s) => {
      const el = document.getElementById(`section-${s.idx + 1}`);
      if (!el) return;
      const o = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.3) {
            setActiveIdx(s.idx);
          }
        },
        { threshold: [0.3, 0.6] }
      );
      o.observe(el);
      observers.push(o);
    });
    const onScroll = () => {
      const h = document.documentElement;
      const progress = h.scrollTop / Math.max(1, h.scrollHeight - h.clientHeight);
      setScrollProgress(Math.min(1, Math.max(0, progress)));
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      observers.forEach((o) => o.disconnect());
      window.removeEventListener('scroll', onScroll);
    };
  }, [steps]);

  const toggleAudio = () => {
    if (!audioRef.current) return;
    if (audioOn) {
      audioRef.current.pause();
      setAudioOn(false);
    } else {
      audioRef.current.volume = 0.35;
      audioRef.current.play().then(() => setAudioOn(true)).catch(() => {});
    }
  };

  const scrollToFirst = () => {
    document.getElementById('section-1')?.scrollIntoView({ behavior: 'smooth' });
  };

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: t('notreProcessus.metaTitle'),
    description: t('notreProcessus.metaDescription'),
    image: `${VIDEO_BASE}/so_2,w_1200,h_630,c_fill,q_auto/${VIDEO_PATH.replace('.mp4', '.jpg')}`,
    step: steps.map((s) => ({
      '@type': 'HowToStep',
      position: s.idx + 1,
      name: s.title,
      text: s.description,
    })),
  };

  return (
    <>
      <SEO
        title={t('notreProcessus.metaTitle')}
        description={t('notreProcessus.metaDescription')}
        url="https://entrepotautomontreal.ca/notre-processus"
        image={`${VIDEO_BASE}/so_2,w_1200,h_630,c_fill,q_auto/${VIDEO_PATH.replace('.mp4', '.jpg')}`}
        schema={structuredData}
      />
      <Navbar />

      <div className="bg-[#05080f] min-h-screen text-white relative overflow-x-hidden">
        <audio ref={audioRef} src={AMBIENT_AUDIO} loop preload="none" />

        {/* HERO */}
        <section
          className="relative h-[100svh] min-h-[640px] flex items-center justify-center overflow-hidden"
          data-testid="process-hero"
        >
          <video
            ref={heroVideoRef}
            src={HERO_VIDEO}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            poster={HERO_POSTER}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-[#05080f]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_70%,rgba(59,130,246,0.15)_0%,transparent_60%)]" />

          <div className="relative z-10 text-center max-w-4xl mx-auto px-4">
            <div className="inline-flex items-center gap-2 mb-6 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-xs font-semibold tracking-[0.25em] uppercase animate-in fade-in duration-1000">
              <Sparkles className="h-3.5 w-3.5 text-blue-400" />
              {t('notreProcessus.heroBadge')}
            </div>
            <h1
              className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-black leading-[1.05] mb-6 tracking-tight animate-in fade-in slide-in-from-bottom-6 duration-1000"
              style={{ fontFamily: "'Montserrat', sans-serif", textShadow: '0 4px 30px rgba(0,0,0,0.5)' }}
            >
              {t('notreProcessus.heroTitle1')}<br className="hidden sm:block" />
              <span className="bg-gradient-to-r from-blue-400 via-white to-blue-400 bg-clip-text text-transparent"> {t('notreProcessus.heroTitle2')}</span>
            </h1>
            <p className="text-base md:text-xl text-white/70 max-w-2xl mx-auto mb-10 animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-300">
              {t('notreProcessus.heroSubtitle')}
            </p>
            <button
              onClick={scrollToFirst}
              data-testid="process-cta-voir"
              className="group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white text-[#05080f] font-bold tracking-wide hover:bg-blue-400 hover:text-white transition-all hover:scale-105 shadow-2xl shadow-blue-500/20 animate-in fade-in slide-in-from-bottom-2 duration-1000 delay-500"
            >
              {t('notreProcessus.heroCta')}
              <ArrowDown className="h-4 w-4 group-hover:translate-y-1 transition-transform" />
            </button>
          </div>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
            <ArrowDown className="h-5 w-5 text-white/50" />
          </div>
        </section>

        <main className="relative">
          {steps.map((s) => (
            <SectionVideo key={s.idx} {...s} stepShort={stepShort} />
          ))}
        </main>

        {/* Final cinematic banner */}
        <section
          className="relative h-[80svh] min-h-[480px] flex items-center justify-center overflow-hidden"
          data-testid="process-final-banner"
        >
          <video
            src={HERO_VIDEO}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster={HERO_POSTER}
            className="absolute inset-0 w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#05080f] via-[#05080f]/70 to-[#05080f]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(59,130,246,0.18)_0%,transparent_70%)]" />

          <div className="relative z-10 text-center px-4">
            <div className="h-[1px] w-24 bg-gradient-to-r from-transparent via-blue-400 to-transparent mx-auto mb-8" />
            <h2
              className="text-4xl md:text-6xl lg:text-7xl font-black leading-tight mb-4 tracking-tight"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Entrepôt Auto Montréal
            </h2>
            <p className="text-lg md:text-2xl text-blue-200/80 italic mb-10" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {t('notreProcessus.finalTagline')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/inventory"
                data-testid="process-cta-inventory"
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-bold tracking-wide transition-all hover:scale-105 shadow-xl shadow-blue-500/30"
              >
                {t('notreProcessus.finalCtaInventory')}
                <ChevronRight className="h-4 w-4" />
              </Link>
              <Link
                to="/contact"
                data-testid="process-cta-contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full border border-white/30 hover:bg-white hover:text-[#05080f] text-white font-bold tracking-wide transition-all"
              >
                {t('notreProcessus.finalCtaContact')}
              </Link>
            </div>
            <div className="h-[1px] w-24 bg-gradient-to-r from-transparent via-blue-400 to-transparent mx-auto mt-12" />
          </div>
        </section>

        {/* Floating Timeline Progress Indicator (desktop only) */}
        <div
          className="hidden lg:flex fixed top-1/2 right-6 -translate-y-1/2 z-40 flex-col items-center gap-3 pointer-events-auto"
          data-testid="process-timeline"
          aria-label={t('notreProcessus.metaTitle')}
        >
          <div className="relative h-[420px] w-[2px] bg-white/10 rounded-full overflow-hidden">
            <div
              className="absolute top-0 left-0 right-0 bg-gradient-to-b from-blue-400 to-blue-600 rounded-full transition-all duration-500 ease-out"
              style={{ height: `${scrollProgress * 100}%` }}
            />
          </div>
          <div className="flex flex-col gap-2 absolute right-0 -mr-1">
            {steps.map((s) => (
              <button
                key={s.idx}
                onClick={() => document.getElementById(`section-${s.idx + 1}`)?.scrollIntoView({ behavior: 'smooth' })}
                className="group relative flex items-center justify-end"
                data-testid={`process-timeline-dot-${s.idx + 1}`}
                style={{ marginTop: s.idx === 0 ? 0 : 'calc((420px - 16px) / 7 - 16px)' }}
              >
                <span
                  className={`absolute right-6 whitespace-nowrap text-[10px] font-bold tracking-[0.2em] uppercase transition-all opacity-0 group-hover:opacity-100 ${
                    activeIdx === s.idx ? '!opacity-100 text-blue-300' : 'text-white/60'
                  }`}
                >
                  {String(s.idx + 1).padStart(2, '0')} · {s.label}
                </span>
                <span
                  className={`block w-3 h-3 rounded-full border transition-all ${
                    activeIdx === s.idx
                      ? 'bg-blue-400 border-blue-300 scale-125 shadow-[0_0_12px_rgba(96,165,250,0.8)]'
                      : 'bg-white/10 border-white/30 group-hover:bg-white/30'
                  }`}
                />
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={toggleAudio}
          data-testid="process-audio-toggle"
          aria-label={audioOn ? t('notreProcessus.audioOn') : t('notreProcessus.audioOff')}
          className="fixed bottom-6 left-6 z-50 w-12 h-12 rounded-full bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/20 hover:scale-110 transition-all flex items-center justify-center text-white shadow-2xl"
        >
          {audioOn ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
          {audioOn && (
            <span className="absolute inset-0 rounded-full border-2 border-blue-400 animate-ping opacity-50" />
          )}
        </button>
      </div>
    </>
  );
};

export default NotreProcessusPage;
